import { PlusOutlined } from '@ant-design/icons';
import { Col, Divider, Flex, Row } from 'antd';
import { useTranslation } from 'translations/hooks';

import { MAX_PARAMETERS } from 'business/data-analysis/constants';
import GraphCardHeader from 'business/data-analysis/pages/graph/components/graph-card-header';
import GraphSection from 'business/data-analysis/pages/graph/components/graph-section';
import { ParameterCountTag } from 'business/data-analysis/pages/graph/components/parameter-count-tag';
import ParameterList from 'business/data-analysis/pages/graph/components/parameter-list';
import {
  useGraphSet,
  useGraphSetDispatch,
} from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { GraphSetDispatchActions } from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useGraph } from 'business/data-analysis/pages/graph/hooks/use-graph';
import { displayTypes } from 'business/data-analysis/pages/graph/types';
import { useMultipleGraphRefsContext } from 'business/providers/graph-reference';
import Button from 'ui/button';
import TrashIcon from 'ui/icons/trash';
import { SimpleCard } from 'ui/SimpleCard';

import styles from './index.module.scss';

const PARAMETER_LIST_WIDTH = 400;

interface Props {
  graphId: string;
}

export const GraphCard = ({ graphId }: Props) => {
  const { t } = useTranslation();
  const { getRef } = useMultipleGraphRefsContext();
  const ref = getRef(graphId);

  const graph = useGraph(graphId);
  const { displayParameterList } = useGraphSet();
  const dispatch = useGraphSetDispatch();

  const openParameterSidebar = () => {
    dispatch({ type: GraphSetDispatchActions.SetCurrentGraphId, graphId });
  };

  const removeAllParameters = () => {
    dispatch({
      type: GraphSetDispatchActions.RemoveParameters,
      graphId,
      parameterUniqueIds: Array.from(graph.parameters.keys()),
    });
  };

  const toggleParameterList = async () => {
    const chartContainer = ref.current?.getOptions().container;
    // Correctly resize the chart when the parameter list is toggled open
    // because the chart resize hook only works with window resize events
    if (chartContainer && !displayParameterList) {
      await ref.current.updateDelta({
        width: chartContainer.clientWidth - PARAMETER_LIST_WIDTH,
      });
    }

    dispatch({ type: GraphSetDispatchActions.ToggleParameterList });
  };

  return (
    <SimpleCard className={styles.graphCard}>
      <Flex vertical gap={20}>
        <GraphCardHeader
          graphId={graphId}
          displayParameterList={displayParameterList}
          toogleDisplayParameterList={() => toggleParameterList()}
        />

        <Divider style={{ margin: 0 }} />

        <Row gutter={[20, 0]} className={styles.graphSectionContainer}>
          {displayParameterList ? (
            <Col xs={10} sm={10} xl={8} xxl={6}>
              <Flex
                vertical
                gap={10}
                align="center"
                className={styles.parameterSection}
              >
                <Flex
                  vertical
                  gap={20}
                  className={styles.parameterSectionHeader}
                >
                  <ParameterCountTag
                    displayTitle
                    selectedParameterCount={graph.parameters.size}
                    maxParameters={
                      graph.displayType === displayTypes.graph
                        ? MAX_PARAMETERS
                        : undefined
                    }
                  />
                  <Flex vertical gap={10}>
                    <Button
                      className={styles.addParameter}
                      type="primary"
                      onClick={openParameterSidebar}
                      icon={<PlusOutlined />}
                    >
                      {t('dataAnalysis.actions.addParameter')}
                    </Button>
                    {graph.parameters.size > 0 ? (
                      <Button
                        icon={
                          <TrashIcon
                            className={styles.removeAllParametersIcon}
                          />
                        }
                        className={styles.removeAllParameters}
                        onClick={removeAllParameters}
                      >
                        {t('dataAnalysis.actions.removeParameters')}
                      </Button>
                    ) : null}
                  </Flex>
                </Flex>
                <ParameterList graphId={graphId} />
              </Flex>
            </Col>
          ) : null}
          <Col
            xs={displayParameterList ? 14 : 24}
            sm={displayParameterList ? 14 : 24}
            xl={displayParameterList ? 16 : 24}
            xxl={displayParameterList ? 18 : 24}
          >
            <GraphSection graphId={graphId} />
          </Col>
        </Row>
      </Flex>
    </SimpleCard>
  );
};
